import { ActionButtons } from '@Common/Controls/ActionButtons';
import { BaseControl, IControlParameters } from '@Common/Controls/BaseControl';
import { IBaseMenuNode, selectMode } from '@Common/Controls/BaseMenu';
import { DragAndDrop, IDragAndDropArgs } from '@Common/Controls/DragAndDrop';
import { Tree } from '@Common/Controls/Tree';
import constants from '@Common/Core/Constant';
import { destroyEvents, safeDestroys } from '@Common/Core/Destroyable';
import StylesHelper from '@Common/Helpers/StylesHelper';
import { SimpleButtonAppearance } from '@InstanceFactory';
import { Scroll } from '@View/Core/Scroll';
import Guid from '../Core/Guid';

export const emptyGuidListItem: IListItemBase = { id: Guid.empty };

export interface IListItemBase<TId extends string | number = string> {
    id?: TId;
    value?: any;
    name?: string;
    separator?: boolean;
}

export interface IListItem<TId extends string | number = string> extends IListItemBase<TId> {
    [data: string]: any;
}

export interface IListParameters extends IControlParameters {
    isTree?: boolean;
    checkable?: boolean;
    selectMode?: string;
    contentStyle?: string;
    itemDefaultStyle?: string;
    itemHoverStyle?: string;
    itemSelectedStyle?: string;
    itemDefaultBorderStyle?: string;
    itemHoverBorderStyle?: string;
    itemSelectedBorderStyle?: string;
    style?: string; // css dublicate
    maxHeight?: number;
    onAppendData?: () => void;
    adjust?: () => void;
    inDropDown?: boolean;
    checkOnClick?: boolean;
    selectOnCheck?: boolean;
    unselectOnClick?: boolean;
    actions?: {
        clearButton?: boolean;
        setButton?: boolean;
        setButtonText?: string;
        searchPanel?: boolean;
        groupsButton?: boolean;
        onSearch?: (arg: { operatorType: string; values: any[] }) => void;
        onSet?: () => void;
        onGroupsButtonClick?: () => void;
        actionButtonsAppearance?: SimpleButtonAppearance;
        actionPanelStyle?: string;
        searchImageStyle?: string;
        triangleImageStyle?: string;
    };
    dragContainment?: any;
    canDrag?: boolean;
    canDrop?: boolean;
    id?: any;
    comparer?: (item1: any, item2: any) => number;
    visibilityComparer?: (item: any) => boolean;

    // can be HTML or com.altosoft.controls object
    emptyMessage?: any;
    sortable?: boolean;

    lazyLoad?: boolean;
    scrollStepPackage?: number;
    allowSorting?: boolean;
    scopes?: string[];
    greedy?: boolean;
    refreshPositions?: boolean;
    stylesHelper?: StylesHelper;
    addItemsUntillScroll?: boolean;
    possibleRightAlign?: boolean;

    canSwitchToExcludedMode?: boolean;
    excludeMode?: boolean;
}

export class List<TId extends string | number = string, ListItem extends IListItemBase<TId> = IListItem<TId>> extends BaseControl {
    private _baseMenu: Tree;
    private readonly _stackPanel: any;
    private _buttons: any[];
    private _onItemDoubleClick: (a, b, c) => undefined;
    private _onItemClick: (a, b, c) => undefined;
    private _onItemCheck: (a, b) => undefined;
    private _onSelectionChanged: (a, b, c) => undefined;
    private _onCheckedChanged: (a, b) => undefined;
    private _onSelectAllChanged: (a) => undefined;
    private _onScrollMove: (a, b) => undefined;
    private _onGetContextMenuItems: (a) => undefined;
    private _onContextMenuItemClick: (menuItem, data) => undefined;
    private _onExcludeModeChanged: (b: boolean) => undefined;
    private _onAppendItem: (a, b) => undefined;
    private _onValidate: (data) => undefined;
    private _onDragStart: (data) => undefined;
    private _onDragOver: (data) => undefined;
    private _onDragOut: (data) => undefined;
    private _onDrop: (data) => undefined;
    private _onSearchToggle: (searchState: boolean) => undefined;
    private _onSort: (a, b, c, d) => undefined;

    constructor(param?) {
        super(
            $.extend(
                {
                    css: null,
                    className: '',
                    width: 'auto',
                    height: 'auto',

                    checkable: true,
                    selectMode: selectMode.simple,
                    contentStyle: constants.driverDefaultStyle.contentStyle,
                    itemDefaultStyle: constants.driverDefaultStyle.itemDefaultStyle,
                    itemHoverStyle: constants.driverDefaultStyle.itemHoverStyle,
                    itemSelectedStyle: constants.driverDefaultStyle.itemSelectedStyle,
                    itemDefaultBorderStyle: constants.driverDefaultStyle.itemDefaultBorderStyle,
                    itemHoverBorderStyle: constants.driverDefaultStyle.itemHoverBorderStyle,
                    itemSelectedBorderStyle: constants.driverDefaultStyle.itemSelectedBorderStyle,
                    maxHeight: null,
                    onAppendData: $.noop,
                    adjust: $.noop,
                    inDropDown: false,
                    actions: {
                        clearButton: false,
                        setButton: false,
                        setButtonText: undefined,
                        searchPanel: false,
                        groupsButton: false,
                        onSearch: $.noop,
                        onSet: $.noop,
                        onGroupsButtonClick: $.noop,
                        actionButtonsAppearance: null,
                        actionPanelStyle: null,
                        searchImageStyle: null
                    },

                    dragContainment: null,
                    canDrag: false,
                    canDrop: false,

                    folderOnTop: false,

                    id: null,
                    comparer: undefined,
                    validator: undefined,
                    visibilityComparer: null,

                    allowKeySearch: true,

                    scopes: null,
                    greedy: false,
                    refreshPositions: false,

                    // can be HTML or controls object
                    emptyMessage: null,

                    sort: null,
                    stylesHelper: null,

                    canSwitchToExcludedMode: false,
                    excludeMode: false
                },
                param
            )
        );

        this._baseMenu = null;
        this._stackPanel = null;
        this._buttons = [];
        this._actions = null;

        this._onItemDoubleClick = $.noop;
        this._onItemClick = $.noop;
        this._onItemCheck = $.noop;
        this._onSelectionChanged = $.noop;
        this._onCheckedChanged = $.noop;
        this._onSelectAllChanged = $.noop;
        this._onScrollMove = $.noop;
        this._onGetContextMenuItems = $.noop;
        this._onContextMenuItemClick = $.noop;
        this._onExcludeModeChanged = $.noop;
        this._onAppendItem = $.noop;
        this._onValidate = $.noop;
        this._onDragStart = $.noop;
        this._onDragOver = $.noop;
        this._onDragOut = $.noop;
        this._onDrop = $.noop;
        this._onSearchToggle = $.noop;
        this._onSort = $.noop;

        this._init();
    }

    private get param() {
        return this._param;
    }

    _init() {
        this._root = $('<div class="list"/>');

        //this._applyDefaultParam();
        this._root.height(this._param.height);
        this._root.width(this._param.width);
        this.setExcludeMode(this._param.canSwitchToExcludedMode && this._param.excludeMode);

        const baseMenuParam = $.extend(this._param, {
            levelStyle: [
                {
                    itemDefaultStyle: this._param.itemDefaultStyle,
                    itemHoverStyle: this._param.itemHoverStyle,
                    itemSelectedStyle: this._param.itemSelectedStyle,
                    itemDefaultBorderStyle: this._param.itemDefaultBorderStyle,
                    itemHoverBorderStyle: this._param.itemHoverBorderStyle,
                    itemSelectedBorderStyle: this._param.itemSelectedBorderStyle
                }
            ],
            isVertical: true,
            allowContextMenu: true,

            // drag and drop fix
            greedy: this._param.greedy
        });

        this._baseMenu = new Tree(baseMenuParam);
        this._baseMenu.onSelectionChanged((a, b, c) => {
            this._onSelectionChanged(a, b, c);
        });
        this._baseMenu.onCheckedChanged((a, b) => {
            this._onCheckedChanged(a, b);
        });
        this._baseMenu.onItemDoubleClick((a, b, c) => {
            this._onItemDoubleClick(a, b, c);
        });
        this._baseMenu.onItemClick((a, b, c) => {
            this._onItemClick(a, b, c);
        });
        this._baseMenu.onItemCheck((a, b) => {
            this._onItemCheck(a, b);
        });
        this._baseMenu.onAppendItem((a, b) => {
            this._onAppendItem(a, b);
        });
        this._baseMenu.onContextMenuItemClick((menuItem, data) => {
            this._onContextMenuItemClick(menuItem, data);
        });
        this._baseMenu.onDragStart(data => {
            this._onDragStart(data);
        });
        this._baseMenu.onDragOver(data => {
            this._onDragOver(data);
        });
        this._baseMenu.onDragOut(data => {
            this._onDragOut(data);
        });
        this._baseMenu.onDrop(data => {
            this._onDrop(data);
        });
        this._baseMenu.onValidate(data => {
            this._onValidate(data);
        });
        this._baseMenu.onScrolledToEnd(() => {
            this._param.onAppendData();
        });
        this._baseMenu.onScrollMove((a, b) => {
            this._onScrollMove(a, b);
        });
        this._baseMenu.onSort((a, b, c, d) => {
            this._onSort(a, b, c, d);
        });
        this._baseMenu.getContextMenuItems = a => this._onGetContextMenuItems(a);

        this._initButtons();
        if (this._buttons && this._buttons.length) {
            this._actions = new ActionButtons({
                actionButtonsAppearance: this._param.actions.actionButtonsAppearance,
                actionPanelStyle: this._param.actions.actionPanelStyle,
                searchImageStyle: this._param.actions.searchImageStyle,
                buttons: this._buttons,
                searchPanel: this._param.actions.searchPanel,
                onSearch: this._param.actions.onSearch,
                resizable: this._param.inDropDown,
                adjust: () => {
                    this.adjust(this._root.outerWidth(), this._root.outerHeight());
                    //param.adjust();
                    //root.forceRender();
                },
                searchToggle: searchState => {
                    this._onSearchToggle(searchState);
                },
                stylesHelper: this._param.stylesHelper
            });
        }

        this._root.append(this._baseMenu.getControl());
        this._actions && this._root.append(this._actions.getControl());

        if (this._param.canDrop) {
            this._baseMenu.bindDroppableControl(this._root);
        }
    }

    _initButtons() {
        this._buttons = [];
        if (this._param.actions.groupsButton) {
            this._buttons.push({
                text: strings.groupsButton,
                click: () => {
                    this._param.actions.onGroupsButtonClick();
                }
            });
        }
        if (this._param.actions.clearButton && this._param.checkable) {
            this._buttons.push({
                text: strings.clearButton,
                click: () => {
                    const checkedCount = this._baseMenu.checkedItems().length;
                    let selectAll = checkedCount < this._baseMenu.getItemsCount();
                    if (this._param.canSwitchToExcludedMode) {
                        if (this._param.excludeMode) {
                            if (!checkedCount) {
                                this._onExcludeModeChanged((this._param.excludeMode = false));
                            }
                        } else {
                            this._onExcludeModeChanged((this._param.excludeMode = true));
                        }
                        selectAll = false;
                        this.setExcludeMode(this._param.excludeMode);
                    }
                    this._baseMenu.checkedItems(selectAll ? this._baseMenu.items() : []);
                    this._onSelectAllChanged(selectAll);
                }
            });
        }
        if (this._param.actions.searchPanel) {
            this._buttons.push({
                html: '&nbsp;',
                icon: this._param.actions.searchImageStyle ? null : 'search.png',
                searchButton: true
            });
        }
        if (this._param.actions.setButton && this._param.checkable) {
            this._buttons.push({
                text: this._param.actions.setButtonText || strings.setButton,
                click: () => {
                    this.param.actions.onSet();
                }
            });
        }
    }

    adjust(width?, height?) {
        if (height == null) {
            height = this.param.height;
        }
        if (width == null) {
            width = this.param.width;
        }

        width && width > 0 && this._root.outerWidth(width);
        /*if (this._baseMenu._rootUl.height() < this._baseMenu.getContentHeight()){

            height = this._baseMenu._rootUl.height();
        }*/
        height && height > 0 && this._root.outerHeight(height);

        if (this._actions && this._buttons && this._buttons.length) {
            height = height - this._actions.getControl().outerHeight(true);
        }

        // adjust base menu
        this._baseMenu.adjust(width, height);
    }

    items(items?: ListItem[]): ListItem[] {
        if (items === undefined) {
            return this._baseMenu.items();
        }

        // check name property
        if (items && items.length && !items[0].name) {
            items = _.map(items, function (i) {
                return $.extend(i, { name: i.value });
            });
        }

        const result = this._baseMenu.items(items);

        return result;
        //refreshScroll();
    }

    nodes(nodes) {
        return this._baseMenu.nodes(nodes);
    }

    setExcludeMode(enabled = false) {
        this._root.toggleClass('excluded', enabled);
    }

    selectedItems(items, customComparer, ready) {
        // check name property
        if (items && items.length && !items[0].name) {
            items = _.map(items, function (i) {
                return $.extend(i, { name: i.value });
            });
        }

        return this._baseMenu.selectedItems(items, customComparer, ready);
    }

    checkedItems(items, customComparer, ready) {
        // check name property
        if (items && items.length && !items[0].name) {
            items = _.map(items, function (i) {
                return $.extend(i, { name: i.value });
            });
        }

        return this._baseMenu.checkedItems(items, customComparer, ready);
    }

    checkItem(id, checked) {
        const item = this.findItemById(id);
        if (item) {
            item.checked = checked;
            this.updateItem(id, item);
        }
    }

    appendItems(items) {
        // check name property
        if (items && items.length && !items[0].name) {
            items = _.map(items, function (i) {
                return $.extend(i, { name: i.value });
            });
        }

        this._baseMenu.appendItems(items);
        return items;
    }

    insertItem(index, item) {
        return this._baseMenu.insert(index, item);
    }

    updateItem(id, item) {
        this._baseMenu.updateItem(id, item);
    }

    prependItems(items) {
        // check name property
        if (items && items[0] && !items[0].name) {
            items = _.map(items, function (i) {
                return $.extend(i, { name: i.value });
            });
        }

        this._baseMenu.prependItems(items);
        return items;
    }

    getItemsCount() {
        return this._baseMenu.getItemsCount();
    }

    empty() {
        this._baseMenu.empty();
    }

    removeItems(comparer) {
        this._baseMenu.removeItems(comparer);
    }

    setNodesVisibility() {
        this._baseMenu.setNodesVisibility();
    }

    setScrollVisibility(visible) {
        this._baseMenu.setScrollVisibility(visible);
    }

    addItemStyle(id, className) {
        this._baseMenu.addItemStyle(id, className);
    }

    removeItemStyle(id, className) {
        this._baseMenu.removeItemStyle(id, className);
    }

    findNodeById(id) {
        return this._baseMenu.findNodeById(id);
    }

    findItem(comparer) {
        return this._baseMenu.findItem(comparer);
    }

    findItemById(id) {
        return this._baseMenu.findItemById(id);
    }

    getItemHeight() {
        return this._baseMenu.getItemHeight();
    }

    getContentHeight() {
        return this._root.height();
    }

    getRealContentHeight() {
        return this._baseMenu.getRealContentHeight();
    }

    getItemValue(data) {
        return this._baseMenu.getItemValue(data);
    }

    getItemRect(itemId) {
        return this._baseMenu.getItemRect(itemId);
    }

    getActionsHeight() {
        return this._actions ? this._actions.getControl().outerHeight(true) : 0;
    }

    getSelectedItemsDescription() {
        return this._baseMenu.getSelectedItemsDescription();
    }

    getCheckedItemsDescription() {
        return this._baseMenu.getCheckedItemsDescription();
    }

    getItemsDescription(items) {
        return this._baseMenu.getItemsDescription(items);
    }

    validate() {
        this._baseMenu.validate();
    }

    getScrollWrapper() {
        return this._baseMenu.getControl();
    }

    getScrollPosition() {
        return this._baseMenu.getScrollPosition();
    }

    selectNextItem() {
        this._baseMenu.selectNextItem();
    }

    selectPrevItem() {
        this._baseMenu.selectPrevItem();
    }

    scrollToItem(item) {
        return this._baseMenu.scrollToItem(item);
    }

    toggleListContent(isVisible) {
        this._baseMenu.getControl().toggle(isVisible);

        if (isVisible) {
            this.adjust(this._param.width, this._param.height);
            this._root.show();
        } else {
            if (this._buttons && this._buttons.length) {
                this.adjust(this._actions.getControl().width(), this._actions.getControl().height());
            } else {
                this._root.hide();
            }
        }
    }

    getRootULHeight() {
        return this._baseMenu.getRootULHeight();
    }

    getVisibleNodes() {
        return this._baseMenu.getVisibleNodes();
    }

    setFocusForRoot() {
        return this._baseMenu.setFocusForRoot();
    }

    getMinRightWidth() {
        if (this._actions) {
            const width = this._root.width();
            const actionBar = this._actions.getControl();
            let barWidth = 0;
            if (actionBar && actionBar.children) {
                actionBar.children().each((i, v) => {
                    if (!$(v).find('input').length) {
                        // ignore item with  search input
                        barWidth += $(v).outerWidth(true);
                    }
                });
                barWidth += actionBar.outerWidth(true) - actionBar.width();
            }
            try {
                this._root.width('auto');
                const expand = this._root.outerWidth(true) - this._root.innerWidth();
                return Math.max(barWidth + expand, this._baseMenu.getMinRightWidth(), 140); //140 - min-width
            } finally {
                this._root.width(width);
            }
        } else {
            return this._baseMenu.getMinRightWidth();
        }
    }

    //Events
    onValidate(fn) {
        $.isFunction(fn) && (this._onValidate = fn);
    }

    onDragStart(fn) {
        $.isFunction(fn) && (this._onDragStart = fn);
    }

    onDragOver(fn) {
        $.isFunction(fn) && (this._onDragOver = fn);
    }

    onDragOut(fn) {
        $.isFunction(fn) && (this._onDragOut = fn);
    }

    onDrop(fn) {
        $.isFunction(fn) && (this._onDrop = fn);
    }

    onSort(fn) {
        $.isFunction(fn) && (this._onSort = fn);
    }

    onContextMenuItemClick(fn) {
        $.isFunction(fn) && (this._onContextMenuItemClick = fn);
    }

    onGetContextMenuItems(fn) {
        $.isFunction(fn) && (this._onGetContextMenuItems = fn);
    }

    onSelectionChanged(fn) {
        $.isFunction(fn) && (this._onSelectionChanged = fn);
    }

    onCheckedChanged(fn) {
        $.isFunction(fn) && (this._onCheckedChanged = fn);
    }

    onItemDoubleClick(fn) {
        $.isFunction(fn) && (this._onItemDoubleClick = fn);
    }

    onItemClick(fn) {
        $.isFunction(fn) && (this._onItemClick = fn);
    }

    onItemCheck(fn) {
        $.isFunction(fn) && (this._onItemCheck = fn);
    }

    onSearchToggle(fn) {
        $.isFunction(fn) && (this._onSearchToggle = fn);
    }

    onAppendItem(fn) {
        $.isFunction(fn) && (this._onAppendItem = fn);
    }

    onScrollMove(fn) {
        $.isFunction(fn) && (this._onScrollMove = fn);
    }

    onSelectAllChanged(fn) {
        $.isFunction(fn) && (this._onSelectAllChanged = fn);
    }

    onExcludeModeChanged(fn) {
        $.isFunction(fn) && (this._onExcludeModeChanged = fn);
    }

    destroy() {
        safeDestroys(this._baseMenu, this._actions);
        super.destroy();
        destroyEvents(this);
    }

    get dragAndDropControl() {
        return this._baseMenu.dragAndDropControl;
    }

    getDragAndDrop() {
        return this._baseMenu.dragAndDropControl;
    }

    refreshScroll() {
        this._baseMenu.refreshScroll();
    }

    // get
    get dragAndDrop() {
        return this._baseMenu.dragAndDropControl;
    }
}

export interface List extends BaseControl {
    _actions: ActionButtons;

    nodes(nodes?: IBaseMenuNode[]): IBaseMenuNode[];

    selectedItems(items?: any[], customComparer?, ready?): any[];

    selectNextItem(): void;

    selectPrevItem(): void;

    appendItems(items: any[]): any[];

    prependItems(items: any[]);

    updateItem(id: any, item: any);

    removeItems(compare: (item: any) => boolean);

    checkedItems(items?: any[], customComparer?, ready?): any[];

    checkItem(id: any, checked: boolean);

    setNodesVisibility();

    getVisibleNodes(): any[];

    setHeight(height: number);

    getScrollWrapper(): JQuery;

    getScrollPosition(): { x: number; y: number };

    refreshScroll();

    setScrollVisibility(visible: boolean);

    scrollToItem(item);

    setStyles();

    addItemStyle(itemId: any, className: string);

    removeItemStyle(itemId: any, className: string);

    empty();

    findItemById(id): any;

    getItemHeight(): number;

    getItemsCount(): number;

    getContentHeight(): number;

    getItemValue(data: any): any;

    getItemRect(itemId: any): { x: number; y: number; width: number; height: number };

    getActionsHeight(): number;

    getSelectedItemsDescription(): string;

    getCheckedItemsDescription(): string;

    getItemsDescription(items: any[]): string;

    onScrollMove(handler: (scroll: Scroll, delta: { deltaX: number; deltaY: number }) => void);

    onSelectionChanged(handler: (oldItems, newItems, clickParam) => void);

    onItemClick(handler: (item: any, a2, a3: JQuery.Event) => void);

    onItemDoubleClick(handler: (item: any, a2, a3: JQuery.Event) => void);

    onItemCheck(handler: (item: any) => void);

    onGetContextMenuItems(handler: (item: any) => any[]);

    onContextMenuItemClick(handler: (item: any, data?: any) => void);

    onCheckedChanged(handler: (beforeItems: any[], afterItems: any[]) => void);

    onExcludeModeChanged(handler: (excludeMode: boolean) => void);

    onAppendItem(handler: (item, li) => void);

    onDragStart(handler);

    onDragOver(handler: (args: IDragAndDropArgs) => void);

    onDragOut(handler);

    onDrop(handler: (args: IDragAndDropArgs) => void);

    onSort(handler: (x, y, z, w) => void);

    validate();

    onValidate(handler: (isValid: boolean) => void);

    onSearchToggle(handler: (toggle: boolean) => void);

    getRealContentHeight(): number;

    destroy();

    getRootULHeight(): number;

    getMinRightWidth(): number;

    insertItem(index: any, item: any): any;

    findNodeById(id: any): any;

    findItem(comparer: any): any;

    getDragAndDrop(): DragAndDrop;
}
